import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import { getAnyProduct, isKitId } from "../config";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CartContext = createContext(null);
export const useCart = () => useContext(CartContext);

const FREE_THRESHOLD = 40;
const FLAT = 4.9;

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem("crush_cart")) || []; } catch { return []; }
  });
  const [open, setOpen] = useState(false);
  const [lastAdded, setLastAdded] = useState(null);

  useEffect(() => {
    localStorage.setItem("crush_cart", JSON.stringify(items));
  }, [items]);

  const key = (id, variant) => `${id}__${variant || ""}`;

  const addItem = useCallback((id, variant = null, quantity = 1) => {
    setItems((prev) => {
      const k = key(id, variant);
      const found = prev.find((i) => key(i.id, i.variant) === k);
      if (found) return prev.map((i) => (key(i.id, i.variant) === k ? { ...i, quantity: i.quantity + quantity } : i));
      return [...prev, { id, variant, quantity }];
    });
    setLastAdded({ id, variant });
    setOpen(true);
  }, []);

  const updateQty = useCallback((id, variant, delta) => {
    setItems((prev) => prev
      .map((i) => (key(i.id, i.variant) === key(id, variant) ? { ...i, quantity: Math.max(1, i.quantity + delta) } : i)));
  }, []);

  const removeItem = useCallback((id, variant) => {
    setItems((prev) => prev.filter((i) => key(i.id, i.variant) !== key(id, variant)));
  }, []);

  const subtotal = items.reduce((s, i) => {
    const p = getAnyProduct(i.id);
    return s + (p ? p.price_eur * i.quantity : 0);
  }, 0);

  const hasKit = items.some((i) => isKitId(i.id));
  const shippingFree = hasKit || subtotal >= FREE_THRESHOLD;
  const shipping = shippingFree ? 0 : (items.length ? FLAT : 0);
  const count = items.reduce((s, i) => s + i.quantity, 0);

  const checkout = async () => {
    const { data } = await axios.post(`${API}/checkout/session`, {
      items: items.map((i) => ({ id: i.id, quantity: i.quantity, variant: i.variant })),
      origin_url: window.location.origin,
    });
    window.location.href = data.checkout_url;
  };

  return (
    <CartContext.Provider value={{
      items, open, setOpen, addItem, updateQty, removeItem, subtotal,
      shipping, shippingFree, hasKit, count, checkout, lastAdded,
    }}>
      {children}
    </CartContext.Provider>
  );
};
